import type { AdminUsageRow } from "../../lib/api";

export type SortField =
  | "email"
  | "artifact_count"
  | "total_bytes"
  | "tokens_7d"
  | "tokens_30d"
  | "last_token_use";

export type SortDir = "asc" | "desc";

export interface UsageFilters {
  q: string;
  role: "all" | "admin" | "user";
  status: "all" | "active" | "deactivated";
}

export const DEFAULT_SORT_FIELD: SortField = "tokens_30d";
export const DEFAULT_SORT_DIR: SortDir = "desc";
export const PAGE_SIZE = 25;

/** Filter rows by email/name search, role, and active status. */
export function filterRows(
  rows: AdminUsageRow[],
  filters: UsageFilters,
): AdminUsageRow[] {
  let result = rows;

  if (filters.q.trim()) {
    const needle = filters.q.trim().toLowerCase();
    result = result.filter(
      (r) =>
        r.email.toLowerCase().includes(needle) ||
        r.display_name.toLowerCase().includes(needle),
    );
  }

  if (filters.role !== "all") {
    result = result.filter((r) => r.role === filters.role);
  }

  if (filters.status !== "all") {
    const wantActive = filters.status === "active";
    result = result.filter((r) => r.is_active === wantActive);
  }

  return result;
}

/** Sort rows by field + direction. Returns a new array (does not mutate). */
export function sortRows(
  rows: AdminUsageRow[],
  field: SortField,
  dir: SortDir,
): AdminUsageRow[] {
  return [...rows].sort((a, b) => {
    let av: number | string;
    let bv: number | string;

    switch (field) {
      case "email":
        av = a.email;
        bv = b.email;
        break;
      case "artifact_count":
        av = a.artifact_count;
        bv = b.artifact_count;
        break;
      case "total_bytes":
        av = a.total_bytes;
        bv = b.total_bytes;
        break;
      case "tokens_7d":
        av = a.tokens_7d;
        bv = b.tokens_7d;
        break;
      case "tokens_30d":
        av = a.tokens_30d;
        bv = b.tokens_30d;
        break;
      case "last_token_use":
        av = a.last_token_use ? new Date(a.last_token_use).getTime() : -1;
        bv = b.last_token_use ? new Date(b.last_token_use).getTime() : -1;
        break;
    }

    if (typeof av === "string") {
      const cmp = av.localeCompare(bv as string);
      return dir === "asc" ? cmp : -cmp;
    }

    const cmp = (av as number) - (bv as number);
    return dir === "asc" ? cmp : -cmp;
  });
}

/** Return the page slice of a sorted+filtered list. */
export function paginateRows<T>(rows: T[], page: number, pageSize: number): T[] {
  return rows.slice(page * pageSize, (page + 1) * pageSize);
}
